import type { Rule, TagKey } from '../types'
import { IND_PACK_RULES } from './ind-pack-rules'
import { ECTD_PACK_RULES } from './ectd-pack-rules'
import { REVIEWERS_GUIDE_PACK_RULES } from './reviewers-guide-pack-rules'

/**
 * Tag → rule-definition mapping.
 *
 * Every rule here is hand-authored against the server-side whitelist
 * enforced by backend/services/rule_validator.py (also mirrored in
 * scripts/validate-tag-rules.mjs, which runs at prebuild). If a property
 * name, icon, target, or operator isn't in the whitelist, the profile
 * PUT will 400.
 *
 * Rules are deduplicated by `id` at the configurator level — selecting
 * two tags that point to the same rule id adds one button, not two.
 *
 * Catalog depth: ALL_RULES is the full registry (~20+ rules). TAG_RULES
 * maps tags to rules and its union MUST stay ≤ MAX_RULES_PER_PROFILE
 * (100 post-A2F-796) or the prebuild validator fails. Specialized rules
 * that aren't worth a dedicated tag (grammar_criteria, passive_flagging,
 * table variants, etc.) live in ALL_RULES only and are reachable via the
 * free-text translate-rule box (backend/services/rule_translation_service.py
 * keyword catalog routes to them without an LLM call).
 */

// ── Rule definitions (deduplicated registry) ──────────────────────────

const R: Record<string, Rule> = {
  // ─── Format: Tables ────────────────────────────────────────────────
  // FDA-style dark navy headers — the sensible default for US regulatory.
  format_tables_fda: {
    id: 'format_tables_fda',
    type: 'format',
    tier: 'free',
    privacy: 'local',
    label: 'Tables',
    icon: 'grid',
    description:
      'FDA-style dark navy headers, alternating rows, 0.5pt borders. 10pt Arial.',
    targets: ['tables'],
    apply: {
      headerRow: {
        'shading.color': '#2E4A8B',
        'font.color': '#FFFFFF',
        'font.bold': true,
        'font.size': 10,
        'font.name': 'Arial',
      },
      evenRow: { 'shading.color': '#F0F4FB' },
      oddRow: { 'shading.color': '#FFFFFF' },
      allCells: { 'font.name': 'Arial', 'font.size': 10 },
      borders: { weight: 0.5, color: '#C0C8D8' },
    },
  },

  format_tables_ema: {
    id: 'format_tables_ema',
    type: 'format',
    tier: 'free',
    privacy: 'local',
    label: 'EMA Tables',
    icon: 'grid',
    description:
      'EMA-style dark green headers, alternating #E8F5E9/white rows, 10pt Arial.',
    targets: ['tables'],
    apply: {
      headerRow: {
        'shading.color': '#1B5E20',
        'font.color': '#FFFFFF',
        'font.bold': true,
        'font.size': 10,
        'font.name': 'Arial',
      },
      evenRow: { 'shading.color': '#E8F5E9' },
      oddRow: { 'shading.color': '#FFFFFF' },
      allCells: { 'font.name': 'Arial', 'font.size': 10 },
      borders: { weight: 0.5, color: '#A5D6A7' },
    },
  },

  format_tables_neutral: {
    id: 'format_tables_neutral',
    type: 'format',
    tier: 'free',
    privacy: 'local',
    label: 'Neutral Tables',
    icon: 'grid',
    description:
      'Neutral gray headers for jurisdiction-agnostic documents. 10pt Arial.',
    targets: ['tables'],
    apply: {
      headerRow: {
        'shading.color': '#4A5568',
        'font.color': '#FFFFFF',
        'font.bold': true,
        'font.size': 10,
        'font.name': 'Arial',
      },
      evenRow: { 'shading.color': '#F7FAFC' },
      oddRow: { 'shading.color': '#FFFFFF' },
      allCells: { 'font.name': 'Arial', 'font.size': 10 },
      borders: { weight: 0.5, color: '#CBD5E0' },
    },
  },

  format_tga_tables: {
    id: 'format_tga_tables',
    type: 'format',
    tier: 'pro',
    privacy: 'local',
    label: 'TGA Tables',
    icon: 'grid',
    description:
      'TGA-style table formatting: navy header with ochre accent row.',
    targets: ['tables'],
    apply: {
      headerRow: {
        'shading.color': '#1C3A5E',
        'font.color': '#FFFFFF',
        'font.bold': true,
        'font.size': 10,
        'font.name': 'Arial',
      },
      evenRow: { 'shading.color': '#FFFFFF' },
      oddRow: { 'shading.color': '#F8F1E4' },
      borders: { weight: 0.5, color: '#B9A376' },
    },
  },

  // ─── Format: Headings ──────────────────────────────────────────────
  format_headings_default: {
    id: 'format_headings_default',
    type: 'format',
    tier: 'free',
    privacy: 'local',
    label: 'Headings',
    icon: 'paintbrush',
    description:
      'Consistent Heading 1–3: bold charcoal, structured spacing.',
    targets: ['paragraphs'],
    match: { style: ['Heading 1', 'Heading 2', 'Heading 3'] },
    apply: {
      'Heading 1': {
        'font.name': 'Arial',
        'font.size': 16,
        'font.bold': true,
        'font.color': '#221610',
        'paragraphFormat.spaceBefore': 24,
        'paragraphFormat.spaceAfter': 12,
      },
      'Heading 2': {
        'font.name': 'Arial',
        'font.size': 14,
        'font.bold': true,
        'font.color': '#221610',
        'paragraphFormat.spaceBefore': 18,
        'paragraphFormat.spaceAfter': 8,
      },
      'Heading 3': {
        'font.name': 'Arial',
        'font.size': 12,
        'font.bold': true,
        'font.color': '#221610',
        'paragraphFormat.spaceBefore': 12,
        'paragraphFormat.spaceAfter': 6,
      },
    },
  },

  format_headings_numbered: {
    id: 'format_headings_numbered',
    type: 'format',
    tier: 'free',
    privacy: 'local',
    label: 'Numbered Headings',
    icon: 'paintbrush',
    description:
      'Heading 1–3 styled for eCTD numbered sections. Pair with Word multilevel numbering.',
    targets: ['paragraphs'],
    match: { style: ['Heading 1', 'Heading 2', 'Heading 3'] },
    apply: {
      'Heading 1': {
        'font.name': 'Arial',
        'font.size': 14,
        'font.bold': true,
        'font.color': '#221610',
        'paragraphFormat.spaceBefore': 18,
        'paragraphFormat.spaceAfter': 8,
      },
      'Heading 2': {
        'font.name': 'Arial',
        'font.size': 12,
        'font.bold': true,
        'font.color': '#221610',
        'paragraphFormat.spaceBefore': 12,
        'paragraphFormat.spaceAfter': 6,
      },
      'Heading 3': {
        'font.name': 'Arial',
        'font.size': 11,
        'font.bold': true,
        'font.color': '#221610',
        'paragraphFormat.spaceBefore': 8,
        'paragraphFormat.spaceAfter': 4,
      },
    },
  },

  format_brand_headings: {
    id: 'format_brand_headings',
    type: 'format',
    tier: 'pro',
    privacy: 'local',
    label: 'Brand Headings',
    icon: 'paintbrush',
    description: 'Brand color palette applied to Heading 1–3.',
    targets: ['paragraphs'],
    match: { style: ['Heading 1', 'Heading 2', 'Heading 3'] },
    apply: {
      'Heading 1': {
        'font.name': 'Arial',
        'font.size': 16,
        'font.bold': true,
        'font.color': '#EC5B13',
        'paragraphFormat.spaceBefore': 24,
        'paragraphFormat.spaceAfter': 12,
      },
      'Heading 2': {
        'font.name': 'Arial',
        'font.size': 14,
        'font.bold': true,
        'font.color': '#221610',
        'paragraphFormat.spaceBefore': 18,
        'paragraphFormat.spaceAfter': 8,
      },
      'Heading 3': {
        'font.name': 'Arial',
        'font.size': 12,
        'font.bold': true,
        'font.color': '#221610',
        'paragraphFormat.spaceBefore': 12,
        'paragraphFormat.spaceAfter': 6,
      },
    },
  },

  // ─── Format: Body + Captions + Lists ───────────────────────────────
  format_body_default: {
    id: 'format_body_default',
    type: 'format',
    tier: 'free',
    privacy: 'local',
    label: 'Body',
    icon: 'type',
    description: '11pt Times New Roman body with 1.15 line spacing.',
    targets: ['paragraphs'],
    match: { style: ['Normal'] },
    apply: {
      Normal: {
        'font.name': 'Times New Roman',
        'font.size': 11,
        'paragraphFormat.lineSpacing': 1.15,
        'paragraphFormat.spaceAfter': 6,
      },
    },
  },

  format_body_arial: {
    id: 'format_body_arial',
    type: 'format',
    tier: 'free',
    privacy: 'local',
    label: 'Body (Arial)',
    icon: 'type',
    description: '10pt Arial body with 1.15 line spacing — FDA default.',
    targets: ['paragraphs'],
    match: { style: ['Normal'] },
    apply: {
      Normal: {
        'font.name': 'Arial',
        'font.size': 10,
        'paragraphFormat.lineSpacing': 1.15,
        'paragraphFormat.spaceAfter': 6,
      },
    },
  },

  format_captions_default: {
    id: 'format_captions_default',
    type: 'format',
    tier: 'free',
    privacy: 'local',
    label: 'Captions',
    icon: 'bookmark',
    description: 'Italic 10pt captions with consistent spacing.',
    targets: ['paragraphs'],
    match: { style: ['Caption'] },
    apply: {
      Caption: {
        'font.name': 'Arial',
        'font.size': 10,
        'font.italic': true,
        'font.color': '#444444',
        'paragraphFormat.spaceBefore': 6,
        'paragraphFormat.spaceAfter': 12,
      },
    },
  },

  format_lists_default: {
    id: 'format_lists_default',
    type: 'format',
    tier: 'free',
    privacy: 'local',
    label: 'Lists',
    icon: 'list',
    description: 'List paragraphs: consistent indent and spacing.',
    targets: ['paragraphs'],
    match: { style: ['List Paragraph'] },
    apply: {
      'List Paragraph': {
        'font.name': 'Arial',
        'font.size': 11,
        'paragraphFormat.spaceAfter': 4,
        'paragraphFormat.firstLineIndent': 0,
      },
    },
  },

  // ─── Scans ─────────────────────────────────────────────────────────
  // A2F-850.7: scoped to the Hidden Text font flag only. The previous
  // any-of conditions added a font.color === '#FFFFFF' literal check
  // and a font.size < 1 check, which only caught a narrow slice of
  // "visual concealment" (a literal #FFFFFF hex, no theme-white, no
  // background comparison; sub-1pt sizing).
  // TODO A2F-850.7: visual color concealment (white-on-white) is a
  // separate ticket — needs background-vs-foreground comparison and
  // theme-color resolution, not a literal hex match.
  scan_hidden_text: {
    id: 'scan_hidden_text',
    type: 'scan',
    tier: 'pro',
    privacy: 'local',
    label: 'Hidden Text',
    icon: 'eye',
    description:
      "Checks Word's Hidden Text formatting flag (Format > Font > Hidden). Does not detect white-on-white or other visual concealment.",
    targets: ['paragraphs'],
    conditions: [
      { property: 'font.hidden', operator: 'equals', value: true },
    ],
    conditionLogic: 'any',
    action: 'flag',
    severity: 'warn',
    message: 'Hidden text — may leak content in submission.',
  },

  scan_missing_alt_text: {
    id: 'scan_missing_alt_text',
    type: 'scan',
    tier: 'pro',
    privacy: 'local',
    label: 'Missing Alt Text',
    icon: 'alert',
    description: 'Flag images without alt text (accessibility + eCTD).',
    targets: ['images'],
    conditions: [{ property: 'image.altText', operator: 'isEmpty' }],
    conditionLogic: 'any',
    action: 'flag',
    severity: 'warn',
    message: 'Image is missing alt text.',
  },

  scan_tiny_text: {
    id: 'scan_tiny_text',
    type: 'scan',
    tier: 'pro',
    privacy: 'local',
    label: 'Tiny Text',
    icon: 'search',
    description: 'Flag paragraphs below 8pt.',
    targets: ['paragraphs'],
    conditions: [{ property: 'font.size', operator: 'lessThan', value: 8 }],
    conditionLogic: 'any',
    action: 'flag',
    severity: 'warn',
    message: 'Paragraph font size is below 8pt.',
  },

  scan_empty_cells: {
    id: 'scan_empty_cells',
    type: 'scan',
    tier: 'pro',
    privacy: 'local',
    label: 'Empty Cells',
    icon: 'flag',
    description: 'Find empty table cells that may signal missing data.',
    targets: ['cells'],
    conditions: [{ property: 'text.content', operator: 'isEmpty' }],
    conditionLogic: 'any',
    action: 'flag',
    severity: 'info',
    message: 'Empty table cell.',
  },

  scan_nonstandard_fonts: {
    id: 'scan_nonstandard_fonts',
    type: 'scan',
    tier: 'pro',
    privacy: 'local',
    label: 'Nonstandard Fonts',
    icon: 'search',
    description:
      'Flag paragraphs using fonts outside Arial / Times New Roman / Calibri.',
    targets: ['paragraphs'],
    conditions: [
      { property: 'font.name', operator: 'notEquals', value: 'Arial' },
      { property: 'font.name', operator: 'notEquals', value: 'Times New Roman' },
      { property: 'font.name', operator: 'notEquals', value: 'Calibri' },
    ],
    // All three must be non-equal → the paragraph uses a font not in the
    // approved set. One match alone is not sufficient (a paragraph in
    // Arial has font.name !== "Times New Roman" but should not flag).
    conditionLogic: 'all',
    action: 'flag',
    severity: 'info',
    message: 'Paragraph uses a font outside the standard regulatory set.',
  },

  // ─── Searches ──────────────────────────────────────────────────────
  search_terminology_fda: {
    id: 'search_terminology_fda',
    type: 'search',
    tier: 'pro',
    privacy: 'local',
    label: 'FDA Terminology',
    icon: 'check',
    description: 'Flag non-preferred terms in FDA submissions.',
    searches: [
      {
        pattern: 'active substance',
        caseSensitive: false,
        severity: 'info',
        message: 'FDA prefers "drug substance" over "active substance".',
        suggestion: 'drug substance',
      },
      {
        pattern: 'active ingredient',
        caseSensitive: false,
        severity: 'info',
        message: 'FDA prefers "drug substance" over "active ingredient" in Module 3.',
        suggestion: 'drug substance',
      },
      {
        pattern: 'finished product',
        caseSensitive: false,
        severity: 'info',
        message: 'FDA prefers "drug product" over "finished product".',
        suggestion: 'drug product',
      },
      // batch / lot inconsistency flag — no auto-suggestion, just informational.
      // Word-bounded via JS regex so "lot" doesn't match "pilot", "ballot",
      // "a lot of", etc. (Office JS paragraph.search with matchWildcards:false
      // is plain substring; Pro executor routes useRegex:true through
      // new RegExp per paragraph, where \b works.)
      {
        pattern: '\\bbatch\\b',
        useRegex: true,
        caseSensitive: false,
        severity: 'info',
        message: 'Consistency check: "batch" and "lot" both appear in regulatory docs — pick one and use it throughout.',
      },
      {
        pattern: '\\blot\\b',
        useRegex: true,
        caseSensitive: false,
        severity: 'info',
        message: 'Consistency check: "batch" and "lot" both appear in regulatory docs — pick one and use it throughout.',
      },
    ],
  },

  search_ich_terms: {
    id: 'search_ich_terms',
    type: 'search',
    tier: 'pro',
    privacy: 'local',
    label: 'ICH Terminology',
    icon: 'check',
    description: 'Align terminology with ICH M4 conventions (EMA/TGA).',
    searches: [
      {
        pattern: 'medicinal product',
        caseSensitive: false,
        severity: 'info',
        message: 'ICH prefers "drug product" in CTD sections.',
        suggestion: 'drug product',
      },
      {
        pattern: 'test article',
        caseSensitive: false,
        severity: 'info',
        message: 'ICH prefers "investigational product" over "test article".',
        suggestion: 'investigational product',
      },
    ],
  },

  search_cover_letter: {
    id: 'search_cover_letter',
    type: 'search',
    tier: 'pro',
    privacy: 'local',
    label: 'Cover Letter',
    icon: 'file',
    description:
      'Validate cover letter required elements per FDA Technical Conformance Guide.',
    // invertMatch: true means "flag if the pattern is NOT found anywhere".
    // Use JS regex (\s*, \d) — the Pro executor dispatches useRegex:true
    // through new RegExp per paragraph, NOT Word's matchWildcards (which
    // is a different DSL). See chrona-addin/src/pro/executor.ts §Search.
    searches: [
      {
        pattern: 'regulatory\\s*(contact|point of contact)',
        useRegex: true,
        caseSensitive: false,
        invertMatch: true,
        severity: 'fail',
        message:
          'No regulatory contact found. FDA requires a regulatory point of contact with email.',
      },
      {
        pattern: 'technical\\s*(contact|point of contact)',
        useRegex: true,
        caseSensitive: false,
        invertMatch: true,
        severity: 'fail',
        message:
          'No technical contact found. FDA requires a technical point of contact with email.',
      },
      {
        pattern: 'virus[\\-\\s]?free|free\\s+of\\s+virus',
        useRegex: true,
        caseSensitive: false,
        invertMatch: true,
        severity: 'fail',
        message:
          'No virus-free statement found. Include AV software, version, and vendor.',
      },
      {
        pattern: '\\d+(\\.\\d+)?\\s*(KB|MB|GB|TB|megabytes|gigabytes)',
        useRegex: true,
        caseSensitive: false,
        invertMatch: true,
        severity: 'warn',
        message:
          'No submission size found. FDA recommends including approximate size in the technical description.',
      },
    ],
  },

  search_legal_placeholders: {
    id: 'search_legal_placeholders',
    type: 'search',
    tier: 'pro',
    privacy: 'local',
    label: 'Placeholders',
    icon: 'flag',
    description: 'Flag TBD, TODO, [brackets], PLACEHOLDER, XX, and Lorem ipsum.',
    searches: [
      { pattern: 'TBD', caseSensitive: false, severity: 'fail',
        message: 'Unresolved placeholder "TBD" — must be replaced before submission.' },
      { pattern: 'TODO', caseSensitive: false, severity: 'warn',
        message: 'Unresolved placeholder "TODO".' },
      { pattern: 'PLACEHOLDER', caseSensitive: false, severity: 'fail',
        message: 'Unresolved placeholder "PLACEHOLDER".' },
      { pattern: 'Lorem ipsum', caseSensitive: false, severity: 'fail',
        message: '"Lorem ipsum" draft text — must be replaced before submission.' },
      { pattern: '\\[INSERT[^\\]]*\\]', useRegex: true, caseSensitive: false,
        severity: 'fail',
        message: 'Unfilled [INSERT ...] placeholder — must be resolved before submission.' },
      { pattern: '\\[[^\\]]+\\]', useRegex: true, severity: 'info',
        message: 'Bracketed placeholder — confirm before submission.' },
      // \bXX\b catches a standalone "XX" token (often used as a TBD
      // shorthand) without flagging legitimate words that contain XX.
      { pattern: '\\bXX\\b', useRegex: true, severity: 'warn',
        message: 'Standalone "XX" — likely a placeholder, confirm before submission.' },
    ],
  },

  search_grammar_data: {
    id: 'search_grammar_data',
    type: 'search',
    tier: 'pro',
    privacy: 'local',
    label: 'data are',
    icon: 'type',
    description: 'Flag "data is" — regulatory convention treats data as plural.',
    searches: [
      {
        pattern: '\\bdata\\s+is\\b',
        useRegex: true,
        caseSensitive: false,
        severity: 'warn',
        message: 'Regulatory convention: "data" is plural — use "data are".',
        suggestion: 'data are',
      },
    ],
  },

  search_grammar_criteria: {
    id: 'search_grammar_criteria',
    type: 'search',
    tier: 'pro',
    privacy: 'local',
    label: 'criteria are',
    icon: 'type',
    description: 'Flag "criteria is" — "criteria" is plural (singular is "criterion").',
    searches: [
      {
        pattern: '\\bcriteria\\s+is\\b',
        useRegex: true,
        caseSensitive: false,
        severity: 'warn',
        message: '"criteria" is plural — use "criteria are". Singular is "criterion".',
        suggestion: 'criteria are',
      },
    ],
  },

  search_passive_flagging: {
    id: 'search_passive_flagging',
    type: 'search',
    tier: 'pro',
    privacy: 'local',
    label: 'Passive Voice',
    icon: 'type',
    description:
      'Flag common passive constructions — style suggestion only, not an error.',
    // No suggestion: passive is often appropriate in regulatory writing
    // (methods sections, compliance statements). Severity stays at info.
    searches: [
      { pattern: 'was performed', caseSensitive: false, severity: 'info',
        message: 'Passive construction "was performed" — consider active voice.' },
      { pattern: 'were conducted', caseSensitive: false, severity: 'info',
        message: 'Passive construction "were conducted" — consider active voice.' },
      { pattern: 'is described', caseSensitive: false, severity: 'info',
        message: 'Passive construction "is described" — consider active voice.' },
    ],
  },
}

// ── Tag → rule list ──────────────────────────────────────────────────
//
// IMPORTANT: the UNION of every array below must stay ≤ 100 rules
// (MAX_RULES_PER_PROFILE, raised to 100 in A2F-796). The prebuild validator
// (scripts/validate-tag-rules.mjs) enforces this at build time. Adding
// a new rule to multiple tags dedupes on id; adding a BRAND-NEW rule
// that isn't in any existing tag's list grows the union by 1.
//
// Specialized rules (format_tables_ema, format_tables_neutral,
// format_body_arial, format_headings_numbered, scan_nonstandard_fonts,
// search_grammar_data, search_grammar_criteria, search_passive_flagging)
// live in ALL_RULES but NOT here — they are reachable via the free-text
// translate-rule path whose backend catalog routes the right keywords.

export const TAG_RULES: Record<TagKey, Rule[]> = {
  // --- What You Write ---
  fda_submissions: [
    R.format_tables_fda,
    R.format_headings_default,
    R.format_captions_default,
    R.scan_hidden_text,
    R.scan_missing_alt_text,
    R.search_terminology_fda,
    R.search_cover_letter,
  ],
  tga_australia: [R.format_tga_tables, R.scan_hidden_text, R.search_ich_terms],
  clinical_protocols: [
    R.format_tables_fda,
    R.format_headings_default,
    R.format_body_default,
  ],
  lab_reports: [
    R.format_tables_fda,
    R.format_headings_default,
    R.scan_empty_cells,
    R.scan_tiny_text,
  ],
  legal_briefs: [R.format_headings_default, R.search_legal_placeholders],
  policy_documents: [
    R.format_headings_default,
    R.format_lists_default,
    R.search_legal_placeholders,
  ],
  technical_reports: [
    R.format_headings_default,
    R.format_body_default,
    R.scan_tiny_text,
    R.scan_empty_cells,
  ],
  sops: [R.format_headings_default, R.format_lists_default],

  // --- What You Care About ---
  ectd_compliant: [R.scan_hidden_text, R.scan_missing_alt_text, R.search_ich_terms],
  company_branding: [R.format_body_default, R.format_brand_headings],
  table_formatting: [R.format_tables_fda],
  consistent_headings: [R.format_headings_default],
  auto_captions: [R.format_captions_default],
  cover_letter_validation: [R.search_cover_letter],
  terminology_checks: [R.search_terminology_fda],
  multi_jurisdiction: [R.search_ich_terms],
}

// A2F-545: IND Starter Pack rules ship in their own module (ind-pack-rules.ts)
// to keep pack content's git blame clean. They are spread into ALL_RULES here
// so the prebuild validator and ALL_RULES_BY_ID lookup resolve them like any
// other rule. They are deliberately NOT added to TAG_RULES — these rules are
// pack-only; discoverability is via the PackRail IND card, not via tags.
//
// A2F-801: eCTD Document Formatting pack rules follow the same own-file
// pattern (ectd-pack-rules.ts) for the same reason. Also pack-only;
// discoverability is via the eCTD pack toggle in /configure and the
// pack-grouped My Chrona taskpane.
//
// A2F-803: NDA Reviewers Guide pack rules (reviewers-guide-pack-rules.ts)
// same own-file pattern. Pack-only; discoverability via the NDA Reviewers
// Guide pack toggle.
export const ALL_RULES: Rule[] = [
  ...Object.values(R),
  ...IND_PACK_RULES,
  ...ECTD_PACK_RULES,
  ...REVIEWERS_GUIDE_PACK_RULES,
]

// ── Pack rule-id → Rule lookup (A2F-537) ─────────────────────────────
//
// Packs store rule *references* (`ruleIds: string[]`); this map is how
// the configurator resolves them into executable Rule objects when
// building the merged rules list. The prebuild validator
// (scripts/validate-tag-rules.mjs) verifies every packs.ts ruleIds
// entry resolves here so a typo fails the build instead of silently
// dropping a pack rule at runtime.
export const ALL_RULES_BY_ID: Record<string, Rule> = Object.fromEntries(
  ALL_RULES.map((r) => [r.id, r]),
)
